import React from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useTheme
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';
import { isConnectionRequestStep, stepLabelKey } from 'utils/campaign-step.utils';
import { InteractionHistory } from '../contact-management.types';
import { MessageDirections } from 'features/co-pilot/campaign-statistics/campaign-statistics-types';

/** Safely format a possibly missing/invalid date; never throws on an Invalid Date. */
const formatDate = (value?: Date | string | number | null): string => {
  if (value === undefined || value === null || value === '') return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return formatDateTime(date.toISOString(), 'Pp');
};

interface Props {
  interactionHistory: InteractionHistory;
  isOpen: boolean;
  isEven: boolean;
  isLast: boolean;

  handleToggleIsOpen(): void;
}

const InteractionHistoryItem: React.FC<Props> = ({
  interactionHistory,
  isOpen,
  isEven,
  isLast,
  handleToggleIsOpen
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <>
      {/* Main Row */}
      <TableRow>
        <TableCell
          sx={{
            borderBottom: !isOpen && !isLast ? 'unset !important' : undefined,
            background: isEven ? theme.palette.backgrounds.main : '#FFF'
          }}
        >
          {interactionHistory.campaignName}
        </TableCell>
        <TableCell
          sx={{
            borderBottom: !isOpen && !isLast ? 'unset !important' : undefined,
            background: isEven ? theme.palette.backgrounds.main : '#FFF'
          }}
        >
          {formatDate(interactionHistory.createDate)}
        </TableCell>
        <TableCell
          sx={{
            borderBottom: !isOpen && !isLast ? 'unset !important' : undefined,
            background: isEven ? theme.palette.backgrounds.main : '#FFF'
          }}
        >
          <IconButton className={classes.button} size="small" onClick={handleToggleIsOpen}>
            {!isOpen ? <ExpandMoreIcon className={classes.icon} /> : <ExpandLessIcon className={classes.icon} />}
          </IconButton>
        </TableCell>
      </TableRow>

      {/* Collapsible Content Row */}
      <TableRow>
        <TableCell style={{ padding: 0, height: 0, border: 'unset' }} colSpan={3}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box sx={{ margin: '8px 0', width: '100%' }}>
              <Table size="small">
                <TableHead>
                  <TableRow className={classes.tableHeadRow}>
                    <TableCell style={{ width: '20%' }}>{t('date')}</TableCell>
                    <TableCell style={{ width: '20%' }}>{t('campaignStep')}</TableCell>
                    <TableCell style={{ width: '60%' }}>{t('message')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(interactionHistory.messages || []).map((message, index) => (
                    <TableRow key={index}>
                      <TableCell className={classes.messageCell}>
                        {formatDate(message.date)}
                      </TableCell>
                      <TableCell className={classes.messageCell}>
                        {message.direction === MessageDirections.RECEIVED
                          ? t('receivedMessage')
                          : !_.isNil(message.step?.functionName)
                            ? t(stepLabelKey(message.step.functionName))
                            : '-'}
                      </TableCell>
                      <TableCell className={classes.messageCell}>
                        {message.text?.trim() ? (
                          message.text
                        ) : message.direction === MessageDirections.SENT &&
                          isConnectionRequestStep(message.step?.functionName) ? (
                          <span className={classes.emptyMessage}>{t('connectionRequestNoNote')}</span>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  tableHeadRow: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#FFF',
    zIndex: 9
  },
  button: {
    padding: '4px'
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.greys.dark
  },
  messageCell: {
    verticalAlign: 'top !important',
    padding: '12px 8px !important'
  },
  emptyMessage: {
    fontStyle: 'italic',
    color: theme.palette.greys.medium
  }
}));

export default InteractionHistoryItem;
